// A tiny event-driven wake-up: `wait()` returns a promise that resolves on the next
// `notify()`. The uploader's pumps block on these instead of polling with setTimeout —
// chained timers in a hidden tab are throttled by Chrome to about one wake-up a minute
// (intensive wake-up throttling after 5 minutes hidden), which starved a background upload.
// Promise resolution is a microtask, never a timer, so it isn't throttled.
export class Signal {
  private waiters: Array<() => void> = [];

  wait(): Promise<void> {
    return new Promise<void>((resolve) => this.waiters.push(resolve));
  }

  notify(): void {
    if (!this.waiters.length) return;
    const ws = this.waiters;
    this.waiters = [];
    for (const w of ws) w();
  }
}

/** Resolve once the browser reports it's back online (immediately if it already is).
 *  Also resolves on `signal.notify()` so a cancel/pause can break the wait. */
export const waitOnline = (signal?: Signal): Promise<void> => {
  if (typeof navigator === "undefined" || navigator.onLine !== false || typeof window === "undefined") {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener("online", done);
      resolve();
    };
    window.addEventListener("online", done);
    signal?.wait().then(done);
  });
};
